function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import { useMemo } from 'react';
import Decimal from 'decimal.js';
import { pathgen } from 'object-path-generator';
import { Box, Button, Column, Divider, FullBox, MenuItem, Row, Select, TextField, Tooltip, Typography } from 'seagull';
import { DiscountModel, serviceCostModel } from "../../models";
import { utils } from "../../utils";
import styles from "./BundlePriceEditorContent.module.scss";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
export const BundlePriceEditorContentTestIds = pathgen('BundlePriceEditorContentTestIds');
export function BundlePriceEditorContent(props) {
  const {
    data,
    service,
    onDataChange,
    onCancel,
    onInputFocus,
    onInputBlur,
    showActions = true
  } = props;
  const {
    bundle
  } = service;
  const suffix = serviceCostModel.isVariablePrice(service.cost) ? '/' + serviceCostModel.formattedUnitNameShort(service.cost) : undefined;
  const {
    bundleRate,
    discount,
    discountType,
    includedServiceRates
  } = data;
  const discountedPrice = useMemo(() => {
    if (!discount) return undefined;
    return utils.applyDiscount(bundleRate, DiscountModel.make(discountType, new Decimal(discount))).toNumber();
  }, [bundleRate, discount, discountType]);
  const onIncludedServiceRateChange = serviceTemplateId => e => {
    const newValue = e.currentTarget.value;
    const asNumber = +newValue;
    if (asNumber >= 0) {
      const newIncludedServiceRates = _objectSpread(_objectSpread({}, includedServiceRates), {}, {
        [serviceTemplateId]: asNumber
      });
      onDataChange(_objectSpread(_objectSpread({}, data), {}, {
        bundleRate: Object.values(newIncludedServiceRates).reduce((sum, rate) => sum + rate, 0),
        includedServiceRates: newIncludedServiceRates
      }));
    }
  };
  const onDiscountChange = e => {
    const newValue = e.currentTarget.value;
    const asNumber = +newValue;
    if (!newValue) {
      onDataChange(_objectSpread(_objectSpread({}, data), {}, {
        discount: ''
      }));
    } else if (asNumber >= 0) {
      onDataChange(_objectSpread(_objectSpread({}, data), {}, {
        discount: asNumber
      }));
    }
  };
  const onDiscountTypeChange = newDiscountType => {
    onDataChange(_objectSpread(_objectSpread({}, data), {}, {
      discountType: newDiscountType
    }));
  };
  const discountError = useMemo(() => {
    if (discount !== '') {
      if (discountType === 'value') {
        if (bundleRate < discount) {
          return "Discount value can't exceed the price";
        }
      } else if (discountType === 'percent' && discount > 100) {
        return 'Provide a value up to 100%';
      }
    }
    return '';
  }, [discount, discountType, bundleRate]);
  return /*#__PURE__*/_jsxs(Column, {
    className: styles.root,
    children: [/*#__PURE__*/_jsxs(Column, {
      className: styles.bundleRate,
      children: [/*#__PURE__*/_jsxs(Row, {
        className: styles.bundleRateRow,
        children: [/*#__PURE__*/_jsx(Tooltip, {
          placement: "top",
          title: "To adjust the bundle rate, update the rates of the included services",
          children: /*#__PURE__*/_jsx(TextField, {
            isMoney: true,
            disabled: true,
            testid: BundlePriceEditorContentTestIds.BundleRate(),
            label: "Rate",
            prefix: "$",
            suffix: suffix,
            value: bundleRate,
            onFocus: onInputFocus,
            onBlur: onInputBlur
          })
        }), /*#__PURE__*/_jsx(TextField, {
          isPercentage: discountType === 'percent',
          isMoney: discountType === 'value',
          inputProps: {
            min: 0
          },
          testid: BundlePriceEditorContentTestIds.BundleDiscount(),
          label: "Discount",
          value: discount,
          onChange: onDiscountChange,
          error: !!discountError,
          helperText: discountError,
          suffix: /*#__PURE__*/_jsx(Box, {
            children: /*#__PURE__*/_jsxs(Select, {
              value: discountType,
              onChange: e => onDiscountTypeChange(e.target.value),
              preventOutline: true,
              "data-testid": BundlePriceEditorContentTestIds.BundleDiscountType(),
              children: [/*#__PURE__*/_jsx(MenuItem, {
                value: "percent",
                children: "%"
              }), /*#__PURE__*/_jsx(MenuItem, {
                disabled: true,
                tooltipProps: {
                  title: 'Discount can only be applied as a percentage'
                },
                value: "value",
                children: "$"
              })]
            })
          }),
          onFocus: onInputFocus,
          onBlur: onInputBlur
        })]
      }), discountedPrice && /*#__PURE__*/_jsxs(Typography, {
        as: "span",
        "data-testid": BundlePriceEditorContentTestIds.FinalPriceSubtitle(),
        children: ["Price after discount:", ' ', /*#__PURE__*/_jsxs(Typography, {
          as: "span",
          weight: "normal",
          "data-testid": BundlePriceEditorContentTestIds.FinalPrice(),
          children: ["$", utils.formatNumberWithCommas(discountedPrice)]
        }), suffix && /*#__PURE__*/_jsxs(Typography, {
          as: "span",
          children: ["\xA0", suffix]
        })]
      })]
    }), (bundle === null || bundle === void 0 ? void 0 : bundle.services) && bundle.services.length > 0 && /*#__PURE__*/_jsxs(Column, {
      className: styles.includedServices,
      children: [/*#__PURE__*/_jsx(Typography, {
        variant: "paragraph1",
        weight: "normal",
        children: "Included services:"
      }), bundle.services.map(includedService => {
        const rate = includedServiceRates[includedService.serviceTemplateId || ''] || 0;
        const hasError = !rate && rate !== 0;
        return /*#__PURE__*/_jsxs(Row, {
          className: styles.includedService,
          "data-testid": BundlePriceEditorContentTestIds.IncludedService(includedService.serviceTemplateId || ''),
          children: [/*#__PURE__*/_jsx(Typography, {
            variant: "paragraph2",
            style: {
              flex: 1
            },
            children: includedService.name
          }), /*#__PURE__*/_jsx(TextField, {
            testid: BundlePriceEditorContentTestIds.IncludedServiceRate(includedService.serviceTemplateId || ''),
            isMoney: true,
            inputProps: {
              min: 0
            },
            label: "Rate",
            prefix: "$",
            width: 120,
            value: rate,
            onChange: onIncludedServiceRateChange(includedService.serviceTemplateId || ''),
            onFocus: onInputFocus,
            onBlur: onInputBlur,
            error: hasError,
            helperText: hasError ? 'Rate is required' : undefined
          })]
        }, includedService.serviceTemplateId);
      })]
    }), showActions && /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(Divider, {
        margin: 0
      }), /*#__PURE__*/_jsxs(FullBox, {
        centered: true,
        flexDirection: "row",
        className: styles.actions,
        children: [/*#__PURE__*/_jsx(Button, {
          type: "button",
          skin: "primaryOutline",
          onClick: onCancel,
          "data-testid": BundlePriceEditorContentTestIds.CancelButton(),
          children: "Cancel"
        }), /*#__PURE__*/_jsx(Button, {
          type: "submit",
          "data-testid": BundlePriceEditorContentTestIds.SaveButton(),
          children: "Save"
        })]
      })]
    })]
  });
}